<script setup lang="ts">
import { ExclamationIcon, ShareIcon } from '@heroicons/vue/outline';
import { BadgeCheckIcon } from '@heroicons/vue/solid';
import { computed, watch, ref } from 'vue';
import { omit, zipWith } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import type { LocationQueryValue } from 'vue-router';
import { useRoute, useRouter } from '#imports';
import CollectionActivity from '~/components/collections/CollectionActivity.vue';
import CollectionMint from '~/components/collections/CollectionMint.vue';
import CollectionNfts from '~/components/collections/CollectionNfts.vue';
import ExploreGridToggle from '~/components/explore/ExploreGridToggle.vue';
import { getApiRoot } from '~/utilities/api';
import { avatarGradientStyles } from '~/utilities/avatar-gradient';
import { formatXchPrice } from '~/utilities/chia-display-utils';
import { BASE_TITLE } from '~/utilities/constants';

definePageMeta({
  backgroundClass: 'bg-neutral-100 dark:bg-neutral-800',
  noPadding: true,
  whiteNavbar: true,
});

const route = useRoute();
const router = useRouter();

let id = route.params?.id;
if (id && id.includes('-col1')) {
  id = `col1${id.split('-col1').pop()}`;
}

const { data: collection } = await useFetch<any>(`${getApiRoot()}/collections/${id}`);
const name = computed(() => collection.value?.name);

const meta = [
  { property: 'og:title', content: `${name.value || 'Unknown'} - Collection ${BASE_TITLE}` },
  { name: 'twitter:title', content: `${name.value || 'Unknown'} - Collection ${BASE_TITLE}` },
  { name: 'twitter:card', content: 'summary_large_image' },
];
if (collection.value?.thumbnail_uri) {
  meta.push({ property: 'og:image', content: collection.value.thumbnail_uri });
  meta.push({ name: 'twitter:image', content: collection.value.thumbnail_uri });
}
useHead({
  title: `${name.value || 'Unknown'} - Collection ${BASE_TITLE}`,
  meta,
} as any);

const gridStyle = useCookie<'grid-compact' | 'grid' | 'table'>('grid-style');
gridStyle.value = gridStyle.value || 'grid';

const tabs = computed(() => {
  const tabs = [
    {
      id: 'items',
      title: 'collection.items',
      count: collection.value?.nft_count || 0,
    },
    {
      id: 'activity',
      title: 'collection.activity',
    },
  ];
  if (collection.value?.active_mints_count) {
    tabs.unshift({
      id: 'mint',
      title: 'collection.mint',
      count: collection.value.active_mints_count,
    });
  }
  return tabs;
});
const selectedTab = ref(route.query.tab ? route.query.tab : collection.value?.unminted_nft_count ? 'mint' : 'items');
watch(selectedTab, (tab) => {
  router.replace({
    path: route.path,
    query: { tab },
  });
});

const routeSorting = route.query?.sort_by;
const sorting = ref<LocationQueryValue>(Array.isArray(routeSorting) ? routeSorting[0] : routeSorting || 'xch_price');
const sortOptions = [
  { value: 'xch_price', label: 'Offer: Low to High' },
  { value: '-created_at', label: 'Offer: New to Old' },
  { value: 'rank', label: 'Rank: Rarest to Common' },
  { value: '-updated_at', label: 'Recently Moved' },
  { value: 'created_at', label: 'Oldest to Newest' },
];
watch(sorting, (sorting) => {
  router.replace({
    path: route.path,
    query: { ...route.query, sort_by: sorting },
  });
});

const selectedTokenId = ref(route.query.token ? route.query.token : 'xch');
watch(selectedTokenId, (token) => {
  router.replace({
    path: route.path,
    query: { token },
  });
});

const { t } = useI18n();
const twitterUrl = ref('');
if (process.client && collection.value) {
  twitterUrl.value = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    collection.value.creator?.twitter_handle
      ? t('collection.shareWithHandle', { handle: collection.value.creator?.twitter_handle, url: window.location.href })
      : t('collection.shareWithoutHandle', { url: window.location.href })
  )}`;
}

const initialAttributeFilters =
  route.query.filter && route.query.filter_value
    ? zipWith(
        Array.isArray(route.query.filter) ? route.query.filter : [route.query.filter],
        Array.isArray(route.query.filter_value) ? route.query.filter_value : [route.query.filter_value],
        (name: LocationQueryValue, value: LocationQueryValue) => ({
          name,
          value,
        })
      )
    : [];

const filterChanged = (attributeFilters: { name: string; value: string }[]) => {
  console.log(attributeFilters);
  const query = omit(route.query, ['filter', 'filter_value']);
  if (attributeFilters.length > 0) {
    query.filter = attributeFilters.map(({ name }) => name);
    query.filter_value = attributeFilters.map(({ value }) => value);
  }
  router.replace({
    path: route.path,
    query,
  });
};
</script>

<template>
  <div class="flex flex-grow flex-col">
    <Error404 v-if="!collection" />
    <div v-else class="flex flex-grow flex-col">
      <div>
        <div
          :class="
            collection.banner_uri ? 'sm:grid-rows-[10rem,8rem,8rem,auto] lg:grid-rows-[14rem,8rem,8rem,auto]' : ''
          "
          class="grid grid-rows-[8rem,6rem,6rem,auto] bg-white dark:bg-black"
        >
          <div class="col-start-1 row-start-1 row-end-3 bg-neutral-800">
            <img
              v-if="collection.banner_uri"
              :src="collection.banner_uri"
              class="h-full w-full object-cover object-center brightness-[75%]"
              :class="[collection.sensitive_content ? 'blur-lg' : '']"
            />
          </div>
          <Container class="col-start-1 row-start-2 row-end-4 flex h-full items-center justify-start">
            <div
              :class="[
                'z-10 h-36 w-36 overflow-hidden rounded-xl ring-8 ring-white dark:ring-black sm:h-48 sm:w-48',
                !collection.thumbnail_uri
                  ? 'flex items-center justify-center bg-gradient-to-br'
                  : 'bg-white dark:bg-black',
              ]"
              :style="avatarGradientStyles(collection.id)"
            >
              <div
                v-if="collection.creator?.verification_state === 2"
                class="flex h-full w-full items-center justify-center"
              >
                <ExclamationIcon class="h-1/2 w-1/2 text-red-600" />
              </div>
              <img
                v-else-if="collection.thumbnail_uri"
                :src="collection.thumbnail_uri"
                class="h-full w-full object-cover object-center"
                :class="[collection.sensitive_content ? 'blur-lg' : '']"
              />
            </div>
            <div class="pl-4 pt-16">
              <NuxtLink
                v-if="twitterUrl"
                rel="canonical"
                :to="twitterUrl"
                target="_blank"
                class="group flex cursor-pointer items-center justify-center gap-1 overflow-hidden rounded-full border px-2 py-2 text-sm font-medium shadow-sm duration-100 ease-in-out hover:border-black hover:ring-1 hover:ring-black dark:hover:border-white dark:hover:ring-white md:px-4"
              >
                <ShareIcon class="h-6 w-6 text-neutral-400 group-hover:text-neutral-500 dark:text-neutral-400" />
                <span class="hidden text-neutral-500 dark:text-neutral-400 md:block">{{ $t('profile.share') }}</span>
              </NuxtLink>
            </div>
          </Container>
          <Container class="col-start-1 row-start-4 row-end-5 mt-4 flex flex-col gap-4">
            <h1
              v-if="collection.name"
              class="flex items-center gap-1 text-2xl font-bold text-neutral-900 dark:text-neutral-50"
            >
              {{ name }}
              <BadgeCheckIcon v-if="collection.creator?.verification_state === 1" class="h-7 w-7 text-emerald-600" />
            </h1>
            <div v-if="collection.creator" class="flex items-center gap-1 dark:text-white">
              {{ $t('collection.by') }}
              <ProfileHandle v-if="collection.creator" :profile="collection.creator" avatar-dimension="2rem" />
              <AddressHandle v-else :address="collection.address" avatar-dimension="2rem" />
            </div>
            <p
              v-if="collection.description"
              class="break-words-properly mt-1 max-h-[15rem] overflow-auto whitespace-pre-wrap text-sm font-medium text-neutral-700 dark:text-neutral-200"
            >
              {{ collection.description }}
            </p>
            <div class="mt-4 overflow-hidden">
              <dl class="-mx-8 -mt-8 flex flex-wrap">
                <div class="flex flex-col px-8 pt-8">
                  <dt class="order-2 text-base font-medium text-neutral-500 dark:text-neutral-400">
                    {{ $t('collection.items') }}
                  </dt>
                  <dd class="order-1 text-2xl font-extrabold text-black dark:text-white sm:text-3xl">
                    {{ (collection.nft_count || 0).toLocaleString('en') }}
                  </dd>
                </div>
                <div v-if="collection.unminted_nft_count" class="flex flex-col px-8 pt-8">
                  <dt class="order-2 text-base font-medium text-neutral-500 dark:text-neutral-400">
                    {{ 'To be minted' }}
                  </dt>
                  <dd class="order-1 text-2xl font-extrabold text-black dark:text-white sm:text-3xl">
                    {{ collection.unminted_nft_count.toLocaleString('en') }}
                  </dd>
                </div>
                <div class="flex flex-col px-8 pt-8">
                  <dt class="order-2 text-base font-medium text-neutral-500 dark:text-neutral-400">Verified Owners</dt>
                  <dd class="order-1 text-2xl font-extrabold text-black dark:text-white sm:text-3xl">
                    {{ (collection.collector_count || 0).toLocaleString('en') }}
                  </dd>
                </div>
                <div class="flex flex-col px-8 pt-8">
                  <dt class="order-2 text-base font-medium text-neutral-500 dark:text-neutral-400">
                    {{ $t('collection.trades') }}
                  </dt>
                  <dd class="order-1 text-2xl font-extrabold text-black dark:text-white sm:text-3xl">
                    {{ (collection.trade_count || 0).toLocaleString('en') }}
                  </dd>
                </div>
                <div v-if="collection.floor_price" class="flex flex-col px-8 pt-8">
                  <dt class="order-2 text-base font-medium text-neutral-500 dark:text-neutral-400">
                    {{ $t('collection.floor') }}
                  </dt>
                  <dd class="order-1 text-2xl font-extrabold text-black dark:text-white sm:text-3xl">
                    {{ formatXchPrice(collection.floor_price) }}
                  </dd>
                </div>
                <div class="flex flex-col px-8 pt-8">
                  <dt class="order-2 text-base font-medium text-neutral-500 dark:text-neutral-400">
                    {{ $t('collection.totalVolume') }}
                  </dt>
                  <dd class="order-1 text-2xl font-extrabold text-black dark:text-white sm:text-3xl">
                    {{ formatXchPrice(collection.volume) }}
                  </dd>
                </div>
              </dl>
            </div>
          </Container>
        </div>
      </div>
      <div class="w-full flex-grow bg-white pt-8 dark:bg-black">
        <Container>
          <div>
            <Tabs v-model="selectedTab" :tabs="tabs">
              <div v-if="selectedTab === 'items'" class="hidden h-full items-center gap-3 px-3 md:flex">
                <TokenPicker v-model="selectedTokenId" :collection-id="collection?.id" />
                <select
                  v-model="sorting"
                  class="block w-full rounded-md border-neutral-300 py-2 pl-3 pr-10 text-base focus:border-black focus:outline-none focus:ring-black dark:border-neutral-600 dark:bg-neutral-800 dark:text-white sm:text-sm"
                >
                  <option v-for="sortOption in sortOptions" :key="sortOption.value" :value="sortOption.value">
                    {{ $t('collection.' + sortOption.label) }}
                  </option>
                </select>
                <ExploreGridToggle v-model="gridStyle" />
              </div>
            </Tabs>
          </div>
          <div v-if="selectedTab === 'items'" class="flex items-center pt-4 md:hidden">
            <select
              v-model="sorting"
              class="mr-3 block w-full rounded-md border-neutral-300 py-2 pl-3 pr-10 text-base focus:border-black focus:outline-none focus:ring-black dark:border-neutral-600 sm:text-sm"
            >
              <option v-for="sortOption in sortOptions" :key="sortOption.value" :value="sortOption.value">
                {{ $t('collection.' + sortOption.label) }}
              </option>
            </select>
            <ExploreGridToggle v-model="gridStyle" />
          </div>
          <div class="py-8">
            <CollectionNfts
              v-if="selectedTab === 'items'"
              :collection="collection"
              :sorting="sorting"
              :grid-style="gridStyle"
              :attribute-filters="initialAttributeFilters"
              :token-id="selectedTokenId"
              @filter-changed="filterChanged"
            />
            <CollectionActivity v-if="selectedTab === 'activity'" :collection="collection" />
            <CollectionMint v-if="selectedTab === 'mint'" :collection="collection" />
          </div>
        </Container>
      </div>
    </div>
  </div>
</template>
